import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Typography,
  Button,
  Tooltip,
  TableRow,
  TableCell,
  Grid,
  Avatar,
  Chip,
  ThemeProvider,
  useTheme
} from "@mui/material";

import { faFire, faBuilding } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  letterSBoolean,
  formatDecimal,
  formatDate,
  ucFirstLetters
} from "@aclymatepackages/formatters";
import { nameToInitials } from "@aclymatepackages/converters";
import { utilitiesProviders } from "@aclymatepackages/lists";
import { subcategories } from "@aclymatepackages/subcategories";
import { mergeDarkTheme } from "@aclymatepackages/themes";

import OfficesGraph from "./OfficesGraph";
import OfficeDetailsSlider from "./OfficeDetailsSlider";
import EditRowTransactions from "../../EditRowTransactions";

import CircularProgressWithLabel from "../../../../atoms/loading/CircularProgressWithLabel";
import FeatureErrorBoundary from "../../../../atoms/FeatureErrorBoundary";
import AvatarGroup from "../../../../atoms/mui/AvatarGroup";
import SortableTable, {
  makeColumnObj
} from "../../../../modules/tables/SortableTable";
import ActionItemsModule from "../../../../modules/ActionItemsModule";
import MultiPartForm from "../../../../inputs/flows/MultipartForm";

import useStatusFilterChips from "../../../../hooks/statusFilterChips";
import useUtilitiesCsvUploader from "../../../../hooks/utilitiesCsvUploader";
import useOfficeCsvUploader from "../../../../hooks/officeCsvUploader";

import AddOfficesForm from "../../../../inputs/offices/AddOfficesForm";

import DashboardViewLayout from "../../../../layouts/DashboardViewLayout";
import { MissingOffices } from "../../../../layouts/missingSettingsLayouts";

import {
  useOfficeStatuses,
  useOfficesData
} from "../../../../../helpers/components/offices";

import { useAccountData } from "../../../../../helpers/firebase";
import { useCompletedOnboardingSteps } from "../../../../../helpers/hooks/companyData";
import useDisplayMeasurementSystem from "../../../../../helpers/hooks/displayMeasurementSystem";
import usePermissions from "../../../../../helpers/hooks/usePermissions";

const LinkedUtilityChip = ({ utilityId, icon, type }) => {
  const { palette } = useTheme();

  const chipColor = palette[type]?.main;
  const findUtilityName = (id) =>
    utilitiesProviders.find(({ utilityId }) => utilityId === id)?.name;

  const tooltipTitleType = () => {
    if (type === "electricity") {
      return "Electric utilities";
    }

    if (type === "gas") {
      return "Gas utilities";
    }

    return "Utilities";
  };

  return (
    <Grid item>
      <Tooltip
        title={`${tooltipTitleType()} are integrated with ${findUtilityName(
          utilityId
        )}`}
      >
        <span>
          <Chip
            icon={
              <Avatar
                style={{
                  fontSize: "1rem",
                  height: "18px",
                  width: "18px",
                  backgroundColor: chipColor,
                  color: "white"
                }}
              >
                {icon}
              </Avatar>
            }
            label={findUtilityName(utilityId)}
            style={{
              color: "white",
              backgroundColor: chipColor
            }}
          />
        </span>
      </Tooltip>
    </Grid>
  );
};

const OfficeRow = ({
  endDate,
  name,
  address,
  sqFootage,
  currentCommuteEmployees,
  facilityType,
  status,
  totalMonthlyCarbonVolume,
  currentUtilities,
  linkedCombinedUtilityId,
  linkedElectricUtilityId,
  linkedGasUtilityId,
  onClick
}) => {
  const {
    electricityInLease,
    heatInLease,
    hasHeat,
    combinedUtility,
    gasUtility,
    electricUtility
  } = currentUtilities || {};

  const { palette } = useTheme();
  const officeStatuses = useOfficeStatuses(endDate);
  const { icon, tooltip, color } = officeStatuses[status] || {};
  const { icon: electricIcon } = subcategories.find(
    ({ subcategory }) => subcategory === "electricity"
  );
  const { icon: gasIcon } = subcategories.find(
    ({ subcategory }) => subcategory === "gas"
  );
  const { icon: combinedIcon } = subcategories.find(
    ({ subcategory }) => subcategory === "utilities"
  );

  const employeeInitials = currentCommuteEmployees.map(({ name }) =>
    nameToInitials(name)
  );

  const displayUtilities = () => {
    const rows = [];

    if (electricityInLease) {
      rows.push(
        <Grid item key="power-lease">
          <strong>Power: </strong> In Lease
        </Grid>
      );
    }

    if (hasHeat !== false && heatInLease) {
      rows.push(
        <Grid item key="heat-lease">
          <strong>Heat: </strong> In Lease
        </Grid>
      );
    }

    const showCombinedUtility =
      combinedUtility &&
      electricityInLease === false &&
      hasHeat !== false &&
      heatInLease === false;

    if (showCombinedUtility) {
      rows.push(
        <Grid item key="combined-utility">
          {linkedCombinedUtilityId ? (
            <LinkedUtilityChip
              utilityId={linkedCombinedUtilityId}
              icon={combinedIcon}
              type="utilities"
            />
          ) : (
            ucFirstLetters(combinedUtility)
          )}
        </Grid>
      );
    } else {
      if (hasHeat !== false && heatInLease === false) {
        rows.push(
          <Grid item key="gas-utility">
            {linkedGasUtilityId ? (
              <LinkedUtilityChip
                utilityId={linkedGasUtilityId}
                icon={gasIcon}
                type="gas"
              />
            ) : (
              <>
                <strong>Gas: </strong> {gasUtility}
              </>
            )}
          </Grid>
        );
      }

      if (electricityInLease === false) {
        rows.push(
          <Grid item key="electric-utility">
            {linkedElectricUtilityId ? (
              <LinkedUtilityChip
                utilityId={linkedElectricUtilityId}
                icon={electricIcon}
                type="electricity"
              />
            ) : (
              <>
                <strong>Electric: </strong> {electricUtility}
              </>
            )}
          </Grid>
        );
      }
    }

    if (!rows.length) {
      return "No utilities configured";
    }

    return (
      <Grid container direction="column" spacing={1}>
        {rows}
      </Grid>
    );
  };

  const rowDetailCells =
    status !== "closed" ? (
      <>
        <TableCell>
          <AvatarGroup
            max={3}
            members={employeeInitials}
            avatarColor={palette.employees.main}
          />
        </TableCell>
        <TableCell>
          {totalMonthlyCarbonVolume
            ? formatDecimal(totalMonthlyCarbonVolume)
            : "No emissions in last month"}
        </TableCell>
        <TableCell>{displayUtilities()}</TableCell>
      </>
    ) : (
      <TableCell colSpan={3}>
        <Typography
          variant="subtitle2"
          align="center"
          color="textSecondary"
        >{`This office is closed and hasn't contributed to your footprint since ${formatDate(
          endDate
        )}`}</Typography>
      </TableCell>
    );

  return (
    <TableRow hover onClick={onClick} style={{ cursor: "pointer" }}>
      <TableCell>
        <Tooltip title={tooltip}>
          <div>
            <FontAwesomeIcon icon={icon} size="2x" style={{ color }} />
          </div>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Typography variant="inherit" display="block">
          {name}
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          {address?.description || ""}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="inherit" display="block">{`${formatDecimal(
          sqFootage
        )} Sq. Ft.`}</Typography>
        <Typography variant="caption" color="textSecondary" display="block">
          {facilityType.label}
        </Typography>
      </TableCell>
      {rowDetailCells}
    </TableRow>
  );
};

const UtilitiesCsvUploader = ({
  selectedCsvUploadOffice,
  setSelectedCsvUploadOffice
}) => {
  const { name: selectedCsvUploadOfficeName, id: selectedCsvUploadOfficeId } =
    selectedCsvUploadOffice || {};

  const [utilitiesCsvUploaderSteps, utilitiesCsvUploaderLoadingValue] =
    useUtilitiesCsvUploader({
      open: selectedCsvUploadOffice,
      setOpen: setSelectedCsvUploadOffice,
      selectedOfficeId: selectedCsvUploadOfficeId,
      selectedOfficeName: selectedCsvUploadOfficeName
    });

  return (
    <MultiPartForm
      type="utilities"
      forms={utilitiesCsvUploaderSteps}
      onClose={() => setSelectedCsvUploadOffice(null)}
      submitLoadingText="Please wait while we upload your CSV..."
      loadingAnimation={
        <ThemeProvider theme={mergeDarkTheme}>
          <CircularProgressWithLabel value={utilitiesCsvUploaderLoadingValue} />
        </ThemeProvider>
      }
    />
  );
};

const OfficeCsvUploader = ({
  selectedOfficeCsvUpload,
  setSelectedOfficeCsvUpload
}) => {
  const {
    name: selectedOfficeName,
    id: selectedOfficeId,
    subcategory
  } = selectedOfficeCsvUpload || {};

  const [officeCsvUploaderSteps, officeCsvUploaderLoadingValue] =
    useOfficeCsvUploader({
      open: selectedOfficeCsvUpload,
      setOpen: setSelectedOfficeCsvUpload,
      selectedOfficeId,
      selectedOfficeName,
      subcategory
    });

  return (
    <MultiPartForm
      type={subcategory}
      forms={officeCsvUploaderSteps}
      onClose={() => setSelectedOfficeCsvUpload(null)}
      submitLoadingText="Please wait while we upload your CSV..."
      loadingAnimation={
        <ThemeProvider theme={mergeDarkTheme}>
          <CircularProgressWithLabel value={officeCsvUploaderLoadingValue} />
        </ThemeProvider>
      }
    />
  );
};

const OfficesViewBlock = ({ viewLoading }) => {
  const { carbonUnitLabel } = useDisplayMeasurementSystem();

  const [{ officesComplete }] = useCompletedOnboardingSteps();
  const [{ isRemote }] = useAccountData();
  const { canEdit } = usePermissions();
  const { offices } = useOfficesData();
  const officeStatuses = useOfficeStatuses();

  const { filterChips, filterByChips } = useStatusFilterChips(
    offices,
    officeStatuses
  );

  const history = useHistory();
  const location = useLocation();
  const selectedOfficeId = new URLSearchParams(location.search).get("id");
  const selectedOffice = offices.find((o) => o.id === selectedOfficeId) ?? null;
  const setSelectedOffice = (office) =>
    office
      ? history.replace({ search: `?id=${office.id}` })
      : history.replace({ search: "" });

  const [addOffices, setAddOffices] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedUtilities, setSelectedUtilities] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [selectedCsvUploadOffice, setSelectedCsvUploadOffice] = useState(null);
  const [selectedOfficeCsvUpload, setSelectedOfficeCsvUpload] = useState(null);

  const buildSubtitle = () => {
    if (isRemote) {
      return "";
    }

    const openOffices = offices.filter(({ status }) => status !== "closed");

    const totalOfficeEmissions = openOffices.reduce(
      (sum, { totalMonthlyCarbonVolume }) =>
        (isNaN(totalMonthlyCarbonVolume) ? 0 : totalMonthlyCarbonVolume) + sum,
      0
    );

    return `You currently have ${openOffices.length} office${letterSBoolean(
      openOffices
    )} that emitted ${formatDecimal(
      totalOfficeEmissions
    )} ${carbonUnitLabel} of CO2 last month.`;
  };

  const companyOfficesColumns = [
    makeColumnObj("STATUS", "status", true),
    makeColumnObj("NAME/ADDRESS", "name", true),
    makeColumnObj("OFFICE DETAILS", "sqFootage", true),
    makeColumnObj("COMMUTING EMPLOYEES"),
    makeColumnObj(
      `${carbonUnitLabel.toUpperCase()} CO2 LAST MONTH`,
      "totalMonthlyCarbonVolume",
      true
    ),
    makeColumnObj("UTILITIES")
  ];

  const findUtilityTypes = () => {
    const officesWithUtilsInLease = offices.filter(
      ({ currentUtilities = {} }) =>
        currentUtilities.electricityInLease ||
        (currentUtilities.hasHeat !== false && currentUtilities.heatInLease)
    );

    const officesWithCombinedUtils = offices.filter(
      ({ currentUtilities = {} }) => !!currentUtilities.combinedUtility
    );

    const officesWithSeparateUtils = offices.filter(
      ({ currentUtilities = {} }) =>
        currentUtilities.gasUtility || currentUtilities.electricUtility
    );

    const typesOfUtilities = [
      { type: officesWithUtilsInLease, caption: "Included in lease" },
      { type: officesWithCombinedUtils, caption: "Combined" },
      { type: officesWithSeparateUtils, caption: "Separate" }
    ];

    const utilitiesOptions = typesOfUtilities.filter(
      ({ type }) => type.length > 0
    );

    return utilitiesOptions.map((option) => ({
      name: option.caption
    }));
  };

  const findFacilityTypes = () => {
    const officesWithFacilityTypes = offices.filter(
      ({ facilityType }) => facilityType
    );

    const facilitiesFromEntries = officesWithFacilityTypes.map(
      ({ facilityType }) => facilityType?.label
    );

    const facilitiesNoDups = !facilitiesFromEntries.length
      ? []
      : facilitiesFromEntries.filter(
          (value, index, displayOffices) =>
            displayOffices.indexOf(value) === index
        );

    return facilitiesNoDups.map((facility) => {
      return { name: ucFirstLetters(String(facility)) };
    });
  };

  const typeFilterFunction = (office) => {
    if (!selectedFacilities.length) {
      return true;
    }

    const lowerCaseFacilities = selectedFacilities.map(({ name }) =>
      name.toLowerCase()
    );

    return lowerCaseFacilities.includes(
      (office.facilityType?.label).toLowerCase()
    );
  };

  const applyFilters = () => {
    const filterByUtilities = ({ currentUtilities = {} }) => {
      if (!selectedUtilities.length) {
        return true;
      }

      const selectedUtilitiesNames = selectedUtilities.map(({ name }) => name);

      if (selectedUtilitiesNames.includes("Included in lease")) {
        return (
          currentUtilities.electricityInLease ||
          (currentUtilities.hasHeat !== false && currentUtilities.heatInLease)
        );
      }

      if (selectedUtilitiesNames.includes("Combined")) {
        return !!currentUtilities.combinedUtility;
      }

      return currentUtilities.electricUtility || currentUtilities.gasUtility;
    };

    return offices.filter(
      (office) =>
        filterByChips(office) &&
        filterByUtilities(office) &&
        typeFilterFunction(office)
    );
  };

  const resetFilters = () => {
    setSelectedFacilities([]);
    return setSelectedUtilities([]);
  };

  const filterRows = [
    {
      icon: faFire,
      title: "Utility Type",
      type: "utilities",
      autocomplete: {
        availableOptions: findUtilityTypes(),
        value: selectedUtilities,
        setValue: setSelectedUtilities,
        label: "Utility Type"
      }
    },
    {
      icon: faBuilding,
      title: "Facility Type",
      type: "facilityType",
      autocomplete: {
        availableOptions: findFacilityTypes(),
        value: selectedFacilities,
        setValue: setSelectedFacilities,
        label: "Facility Type"
      }
    }
  ];

  const filteredOffices = applyFilters();

  const selectOfficeFromGraph = (id) =>
    history.replace({ search: `?id=${id}` });

  return (
    <>
      {!!selectedCsvUploadOffice && (
        <UtilitiesCsvUploader
          selectedCsvUploadOffice={selectedCsvUploadOffice}
          setSelectedCsvUploadOffice={setSelectedCsvUploadOffice}
        />
      )}
      {!!selectedOfficeCsvUpload && (
        <OfficeCsvUploader
          selectedOfficeCsvUpload={selectedOfficeCsvUpload}
          setSelectedOfficeCsvUpload={setSelectedOfficeCsvUpload}
        />
      )}
      {addOffices && (
        <AddOfficesForm open={addOffices} setOpen={setAddOffices} />
      )}
      {selectedOffice && (
        <OfficeDetailsSlider
          setSelectedOffice={setSelectedOffice}
          setSelectedTransaction={setSelectedTransaction}
          office={selectedOffice}
          selectedCsvUploadOffice={selectedCsvUploadOffice}
          setSelectedCsvUploadOffice={setSelectedCsvUploadOffice}
          setSelectedOfficeCsvUpload={setSelectedOfficeCsvUpload}
        />
      )}
      {selectedTransaction && (
        <EditRowTransactions
          transaction={selectedTransaction || {}}
          setIsSlideOpen={setSelectedTransaction}
          saveButtonText="Save Changes"
        />
      )}
      {viewLoading || (officesComplete && !isRemote) ? (
        <DashboardViewLayout
          type="offices"
          title="Offices"
          subtitle={buildSubtitle()}
          viewLoading={viewLoading}
          isFiltered={selectedUtilities.length || selectedFacilities.length}
          filterPopperProps={{
            filterRows,
            resetFilters,
            title: "Filter Offices"
          }}
          chips={filterChips}
          primaryAction={
            canEdit && (
              <Button
                color="primary"
                variant="contained"
                onClick={() => setAddOffices(true)}
              >
                Add New Offices
              </Button>
            )
          }
          graph={
            <OfficesGraph
              dataArray={[...filteredOffices].sort(
                (a, b) =>
                  a.totalMonthlyCarbonVolume - b.totalMonthlyCarbonVolume
              )}
              setSelectedObject={selectOfficeFromGraph}
            />
          }
          table={
            <SortableTable
              rows={[...filteredOffices].sort(
                (a, b) => b.severity - a.severity
              )}
              rowComponent={(office) => (
                <OfficeRow
                  key={`office-row-${office.id}`}
                  {...office}
                  onClick={() => setSelectedOffice(office)}
                />
              )}
              columns={companyOfficesColumns}
            />
          }
          footer={<ActionItemsModule title="Offices" type="offices" />}
        />
      ) : (
        <MissingOffices />
      )}
    </>
  );
};

const OfficesView = (props) => (
  <FeatureErrorBoundary featureName="Office Management">
    <OfficesViewBlock {...props} />
  </FeatureErrorBoundary>
);
export default OfficesView;
